import React from 'react';

export class DataRenderer {
  // Helper function to check if data is a simple key-value table
  static isSimpleKeyValueTable(statistics) {
    return statistics.length > 0 && 
           Object.keys(statistics[0]).length === 2 &&
           statistics.every(item => {
             const values = Object.values(item);
             return values.length === 2 && 
                    typeof values[0] === 'string' && 
                    typeof values[1] === 'number';
           });
  }

  // Render simple table with two columns (key-value pairs)
  static renderSimpleTable(statistics) {
    const [keyField, valueField] = Object.keys(statistics[0]);
    const sortedData = [...statistics].sort((a, b) => b[valueField] - a[valueField]);
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                {keyField}
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                {valueField}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 text-sm text-gray-600 border-b align-top">
                  <span className="font-medium text-gray-700" title={item[keyField]}>
                    {item[keyField]}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 border-b align-top">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold text-sm">
                    {item[valueField]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Render array of objects as cards
  static renderObjectArray(value) {
    if (!Array.isArray(value) || value.length === 0) {
      return <span className="text-gray-500">No data</span>;
    }

    // Try to find a count field (could be 'count', 'Count', etc.)
    const countField = Object.keys(value[0]).find(key => 
      key.toLowerCase().includes('count') || typeof value[0][key] === 'number'
    );
    
    // Sort by count field if found, otherwise keep original order
    const sortedData = countField 
      ? [...value].sort((a, b) => (b[countField] || 0) - (a[countField] || 0))
      : [...value];
    
    return (
      <div className="space-y-1">
        {sortedData.map((item, idx) => {
          const entries = Object.entries(item);
          const countEntry = entries.find(([key]) => key === countField);
          const otherEntries = entries.filter(([key]) => key !== countField);
          
          return (
            <div key={idx} className="flex justify-between items-center bg-gray-50 px-2 py-1 rounded text-sm">
              <div className="flex-1 mr-2">
                {otherEntries.map(([key, val], entryIdx) => (
                  <div key={entryIdx} className="truncate" title={`${key}: ${val}`}>
                    <span className="font-medium text-gray-700">{String(val)}</span>
                  </div>
                ))}
              </div>
              {countEntry && (
                <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
                  {countEntry[1]}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Render single object dynamically
  static renderSingleObject(value) {
    const entries = Object.entries(value);
    
    // Try to find a count field
    const countEntry = entries.find(([key]) => 
      key.toLowerCase().includes('count') || typeof value[key] === 'number'
    );
    const otherEntries = entries.filter(([key]) => key !== countEntry?.[0]);
    
    return (
      <div className="flex justify-between items-center bg-gray-50 px-2 py-1 rounded text-sm">
        <div className="flex-1 mr-2">
          {otherEntries.map(([key, val], idx) => (
            <div key={idx} className="truncate" title={`${key}: ${val}`}>
              <span className="text-xs text-gray-500">{key}:</span> {' '}
              <span className="font-medium text-gray-700">{String(val)}</span>
            </div>
          ))}
        </div>
        {countEntry && (
          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
            {countEntry[1]}
          </span>
        )}
      </div>
    );
  }

  // Render complex object as formatted JSON
  static renderComplexObject(value) {
    return (
      <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-32">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  // Render cell value based on its type
  static renderCellValue(value) {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (Array.isArray(value)) {
      return DataRenderer.renderObjectArray(value);
    }
    
    if (typeof value === 'object') {
      // Check if it's a simple object with 2-3 fields that might represent structured data
      const keys = Object.keys(value);
      if (keys.length <= 3 && keys.length > 0) {
        return DataRenderer.renderSingleObject(value);
      }
      // For complex objects, show as JSON
      return DataRenderer.renderComplexObject(value);
    }
    
    return String(value);
  }

  // Render complex table with multiple columns
  static renderComplexTable(statistics) {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              {Object.keys(statistics[0] || {}).map((key) => (
                <th key={key} className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                  {key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statistics.map((item, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {Object.values(item).map((value, valueIndex) => (
                                  <td key={valueIndex} className="px-4 py-3 text-sm text-gray-600 border-b align-top">
                  {DataRenderer.renderCellValue(value)}
                </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Main statistics rendering function
  static render(statistics) {
    if (!statistics || !Array.isArray(statistics)) {
      return null;
    }
    
    return DataRenderer.isSimpleKeyValueTable(statistics) 
      ? DataRenderer.renderSimpleTable(statistics)
      : DataRenderer.renderComplexTable(statistics);
  }
} 